import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import SaveButton from '@ui-elements/SaveButton';
import BasicModal from '@ui-parts/BasicModal';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { updateAdministratorRoles, callThunk, fetchAdminAndRoles } from '~/api';
import { GMOASK_STATUS } from '~/constants/api';

const CheckBoxWrapper = styled.span`
  flex: '100%';
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`;

const ModalFooterInner = styled.div`
  padding-bottom: 28px;
`;

const StyledButton = styled(SaveButton)`
  &&&.MuiButton-root.MuiButton-contained {
    font-size: ${FONT_SIZES.body3};
    color: ${PALETTE.white};
    background-color: #4aa7f7;
    box-shadow: 1px 2px 2px #00000014;
    width: 148px;
    height: 40px;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &.MuiCheckbox-root {
    color: '#C5D5DD';
    &.Mui-checked {
      background-color: ${PALETTE.blue.main};
    }
  }
  &.Mui-checked {
    &::before {
      background-color: ${PALETTE.white};
      border: 7px solid ${PALETTE.primary.main};
    }
  }
  .MuiSvgIcon-root {
    font-size: 24px;
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  padding: 15px 15px 10px;
  font-size: ${FONT_SIZES.body3};
  color: ${PALETTE.text.main};
  height: 44px;
  padding: 8px 10px;
  border-bottom: 1px solid #c5d5dd;
`;

const Label = styled.span`
  font-size: ${FONT_SIZES.bodyDefault};
`;

function RolesModal(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { open, handleClose, options, rolesAdmin, setAlert } = props;
  const [checked, setChecked] = useState([]);

  const handleChange = (event) => {
    const id = parseInt(event.target.name, 10);
    const newValue = event.target.checked ? [...checked, id] : checked.filter((val) => val !== id);
    setChecked(newValue);
  };
  const handleClickSubmit = async () => {
    const { payload } = await dispatch(
      callThunk({
        action: updateAdministratorRoles,
        payload: {
          administratorId: rolesAdmin.id,
          roles: checked,
        },
      })
    );
    if (payload.status === GMOASK_STATUS.OK) {
      setAlert(true);
      handleClose();
    }
    dispatch(
      callThunk({
        action: fetchAdminAndRoles,
      })
    );
  };

  useEffect(() => {
    setChecked(rolesAdmin.roles);
  }, [rolesAdmin.roles]);

  return (
    <BasicModal
      open={open}
      titleProps={{
        title: t('administrators.roles.modal.title'),
      }}
      handleClose={() => {
        handleClose();
      }}
      buttons={[
        <ModalFooterInner key="ModalFooterInner">
          <StyledButton
            key="button1"
            handleClick={() => {
              handleClickSubmit();
            }}
            label={t('appCommon.setting')}
          />
        </ModalFooterInner>,
      ]}
      dataTestId="RolesModal"
      width="600px"
    >
      <FlexWrapper>
        {options.map((option) => (
          <StyledFormControlLabel
            key={option.id}
            control={
              <CheckBoxWrapper key={option.id}>
                <StyledCheckbox
                  name={option.id.toString()}
                  checked={checked.includes(option.id)}
                  onChange={handleChange}
                  color="primary"
                />
              </CheckBoxWrapper>
            }
            label={<Label>{option.name}</Label>}
          />
        ))}
      </FlexWrapper>
    </BasicModal>
  );
}
RolesModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  rolesAdmin: PropTypes.shape({
    id: PropTypes.number.isRequired,
    roles: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
  setAlert: PropTypes.func,
};

export default RolesModal;
